import { FC, useState } from 'react';
import {
  useResetAdvancedCartDetails,
  useSetSingleItemName,
  useSingleLenderCartAmountError,
} from '../services/cartManager';
import { logger } from '@chargeafter/logger';
import { useNavigate } from 'react-router-dom';
import { ButtonName } from '../constants';
import { useGetInteractionOptions } from '../services/configManager';
import { useGetSelectedConsumerStatus } from '../services/customersTabManager';
import {
  useGetApprovedAmount,
  useCheckout,
  useCreateCharge,
  useGetConsumer,
  useGetApprovingLender,
} from '../services/flowManager';
import {
  useGetCheckoutConfiguration,
  useUpdateConfigurationHandler,
} from '../services/lendersConfigurationManager';
import { MuiTrackerButton } from './TrackerButtonWrapper';
import { useSetCurrentModal } from '../services/modalManager';
import { styled } from '@mui/material';
import { useGetLendersCheckoutSettings } from '../services/configManagerNew';
import { useDeviceSelectionModal } from '../modals/useDeviceSelectionModal';
import { getFlags } from '@chargeafter/feature-flag';
import {
  LinkFlow,
  useGetLinkInteraction,
  useSetLinkInteraction,
} from '../services/sendLinkManager';
import { useIsCartInvalid } from '../hooks/useIsCartInvalid';
import { useCartLimitationModal } from '../modals/useCartLimitationModal';

const StyledCheckoutButton = styled(MuiTrackerButton)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1),
  borderRadius: theme.spacing(1),
}));

type CheckoutButtonProps = {};
const CheckoutButton: FC<CheckoutButtonProps> = () => {
  const [loading, setLoading] = useState(false);
  const stopLoading = () => setLoading(false);

  const approvedAmount = useGetApprovedAmount();
  const setSingleItemName = useSetSingleItemName();
  const resetAdvancedCart = useResetAdvancedCartDetails();
  const updateCurrentModal = useSetCurrentModal();
  const openSendLinkModal = () => updateCurrentModal('sendLink');
  const { ffSalesAppDeviceSelectionModal } = getFlags();

  const linkInteraction = useGetLinkInteraction();
  const updateLinkInteraction = useSetLinkInteraction();
  const isCartInvalid = useIsCartInvalid();

  const { expressCheckout: expressCheckoutAllowed } =
    useGetInteractionOptions();
  const checkout = useCheckout(stopLoading);
  const createCharge = useCreateCharge();
  const navigate = useNavigate();
  const consumer = useGetConsumer();
  const selectedConsumerStatus = useGetSelectedConsumerStatus();

  const isConsumerPending =
    selectedConsumerStatus === 'CheckoutInProgress' ||
    selectedConsumerStatus === 'PendingConsumer' ||
    selectedConsumerStatus === 'PendingLender';
  const expressCheckout =
    !consumer || (!isConsumerPending && !!selectedConsumerStatus);

  const checkoutConfig = useGetCheckoutConfiguration();
  const mainLenderCheckoutConfig =
    checkoutConfig?.lendersConfigurations[0].configuration;

  const invalidCartState =
    (!expressCheckoutAllowed ||
      !mainLenderCheckoutConfig?.allowExpressCheckout) &&
    !consumer &&
    !approvedAmount;

  const shouldCheckoutButtonBeDisabled =
    isCartInvalid || invalidCartState || loading;

  const selectedLender = useGetApprovingLender();
  const lendersCheckoutSettings = useGetLendersCheckoutSettings();
  const lenderForbidsMerchantDevice = lendersCheckoutSettings?.some(
    (lenderSettings) => !lenderSettings?.options.allowMerchantDevice,
  );

  const getContinueOnThisDevice = useDeviceSelectionModal();

  const getCartLimitation = useCartLimitationModal();

  const updateConfigurationHandler = useUpdateConfigurationHandler();

  return (
    <StyledCheckoutButton
      loading={loading}
      loaderColor="white"
      buttonName={ButtonName.StartCheckout}
      variant="contained"
      onClick={async () => {
        //TODO: remove this log when incident #34555 resolved
        logger.log('Clicked on checkout button in create cart tab', {
          filename: 'CreateCartTab.tsx',
          function: 'CheckoutButton onClick',
        });

        const checkoutAllowed =
          !selectedLender ||
          checkoutConfig?.lendersConfigurations.find(
            (lender) => lender.id === selectedLender,
          );

        if (checkoutAllowed) {
          const shouldContinue = await getCartLimitation();
          if (!shouldContinue) {
            return;
          }

          if (ffSalesAppDeviceSelectionModal && lenderForbidsMerchantDevice) {
            const continueOnThisDevice = await getContinueOnThisDevice();
            if (!continueOnThisDevice) {
              updateLinkInteraction({
                ...linkInteraction,
                action: expressCheckout
                  ? LinkFlow.ExpressCheckout
                  : LinkFlow.Checkout,
              });
              openSendLinkModal();
              return;
            }
          }
        }

        setLoading(true);
        updateConfigurationHandler();

        //TODO: remove this log when incident #34555 resolved
        logger.log('About to call checkout function ', {
          filename: 'CreateCartTab.tsx',
          function: 'CheckoutButton onClick',
        });

        const checkoutResult = await checkout(undefined, expressCheckout);

        if (checkoutResult) {
          setSingleItemName('');
          resetAdvancedCart();
          navigate('/confirm');
          await createCharge(
            checkoutResult.settlementOption,
            checkoutResult?.token,
            checkoutResult.lender.id,
          );
        }
      }}
      disabled={shouldCheckoutButtonBeDisabled}
      color="primary"
      fullWidth
      data-testid="purchase-btn"
    >
      On this device
    </StyledCheckoutButton>
  );
};

export default CheckoutButton;
